import { supabase } from './client';

export interface CustomGarmentType {
  id: string;
  empresa_id: string;
  nome: string;
  is_predefined: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CustomGarmentField {
  id: string;
  empresa_id: string;
  garment_type_id: string;
  field_name: string;
  field_label: string;
  field_type: 'number' | 'text' | 'select';
  unidade?: string;
  obrigatorio: boolean;
  ordem: number;
  created_at: string;
  updated_at: string;
}

// ===== TIPOS DE PEÇA =====

export async function getGarmentTypes(empresaId: string): Promise<CustomGarmentType[]> {
  try {
    const { data, error } = await supabase
      .from('custom_garment_types')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('nome', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ Erro ao buscar tipos de peça:', error);
    throw error;
  }
}

export async function createGarmentType(
  empresaId: string,
  nome: string,
  userId: string
): Promise<{ ok: boolean; id?: string; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('custom_garment_types')
      .insert([{ empresa_id: empresaId, nome, created_by: userId }])
      .select('id')
      .single();

    if (error) throw error;
    return { ok: true, id: data.id };
  } catch (error) {
    console.error('❌ Erro ao criar tipo de peça:', error);
    return { ok: false, error: 'Erro ao criar tipo de peça' };
  }
}

export async function updateGarmentType(
  id: string,
  nome: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('custom_garment_types')
      .update({ nome, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
    return { ok: true };
  } catch (error) {
    console.error('❌ Erro ao atualizar tipo de peça:', error);
    return { ok: false, error: 'Erro ao atualizar tipo de peça' };
  }
}

export async function deleteGarmentType(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('custom_garment_types')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { ok: true };
  } catch (error) {
    console.error('❌ Erro ao deletar tipo de peça:', error);
    return { ok: false, error: 'Erro ao deletar tipo de peça' };
  }
}

// ===== CAMPOS CUSTOMIZADOS =====

export async function getGarmentFields(
  empresaId: string,
  garmentTypeId: string
): Promise<CustomGarmentField[]> {
  try {
    const { data, error } = await supabase
      .from('custom_garment_fields')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('garment_type_id', garmentTypeId)
      .order('ordem', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ Erro ao buscar campos:', error);
    throw error;
  }
}

export async function createGarmentField(
  empresaId: string,
  garmentTypeId: string,
  field: Omit<CustomGarmentField, 'id' | 'empresa_id' | 'garment_type_id' | 'created_at' | 'updated_at'>
): Promise<{ ok: boolean; id?: string; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('custom_garment_fields')
      .insert([
        {
          empresa_id: empresaId,
          garment_type_id: garmentTypeId,
          ...field
        }
      ])
      .select('id')
      .single();

    if (error) throw error;
    return { ok: true, id: data.id };
  } catch (error) {
    console.error('❌ Erro ao criar campo:', error);
    return { ok: false, error: 'Erro ao criar campo' };
  }
}

export async function updateGarmentField(
  id: string,
  field: Partial<Omit<CustomGarmentField, 'id' | 'empresa_id' | 'garment_type_id' | 'created_at' | 'updated_at'>>
): Promise<{ ok: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('custom_garment_fields')
      .update({ ...field, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
    return { ok: true };
  } catch (error) {
    console.error('❌ Erro ao atualizar campo:', error);
    return { ok: false, error: 'Erro ao atualizar campo' };
  }
}

export async function deleteGarmentField(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('custom_garment_fields')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { ok: true };
  } catch (error) {
    console.error('❌ Erro ao deletar campo:', error);
    return { ok: false, error: 'Erro ao deletar campo' };
  }
}
